/**
 * Interactive Visitor Map Component
 *
 * Displays all visitor locations on a world map with:
 * - Markers showing exact coordinates
 * - Popups with company info, timezone, currency
 * - Color-coded markers for VPN/datacenter/mobile
 * - Clustering for proximity
 */

import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default icon issue in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export interface MapLocation {
  ip: string;
  lat: number;
  lon: number;
  city: string | null;
  country: string | null;
  country_code: string | null;
  company_name: string | null;
  company_domain: string | null;
  geo_isp: string | null;
  geo_timezone: string | null;
  geo_currency: string | null;
  geo_is_vpn: number | null;
  geo_is_datacenter: number | null;
  geo_is_mobile: number | null;
  last_seen: number;
  visit_count: number;
  last_url: string | null;
}

interface VisitorMapProps {
  siteId: string;
  locations: MapLocation[];
  newVisitorIps?: Set<string>;
  onLocationClick?: (location: MapLocation) => void;
}

// Custom marker icons based on visitor type
const createCustomIcon = (location: MapLocation, isNew: boolean = false) => {
  let color = '#3b82f6'; // blue - normal visitor
  let iconHtml = '📍';

  if (location.geo_is_datacenter === 1) {
    color = '#ef4444'; // red - bot/datacenter
    iconHtml = '🤖';
  } else if (location.geo_is_vpn === 1) {
    color = '#f59e0b'; // amber - VPN
    iconHtml = '🔒';
  } else if (location.geo_is_mobile === 1) {
    color = '#8b5cf6'; // purple - mobile
    iconHtml = '📱';
  } else if (location.company_name) {
    color = '#10b981'; // green - identified company
    iconHtml = '🏢';
  }

  const pulseAnimation = isNew ? `
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.2); opacity: 0.8; }
    }
    animation: pulse 1s ease-in-out infinite;
  ` : '';

  return L.divIcon({
    html: `
      <style>${pulseAnimation}</style>
      <div style="
        background-color: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${pulseAnimation ? 'animation: pulse 1s ease-in-out infinite;' : ''}
      ">${iconHtml}</div>
      ${isNew ? `<div style="
        position: absolute;
        top: -8px;
        right: -8px;
        background-color: #ef4444;
        color: white;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: bold;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">NEW</div>` : ''}
    `,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Component to auto-fit map to show all markers
function FitBounds({ locations }: { locations: MapLocation[] }) {
  const map = useMap();

  useEffect(() => {
    if (locations.length === 0) return;

    const bounds = L.latLngBounds(
      locations.map(loc => [loc.lat, loc.lon] as LatLngExpression)
    );

    map.fitBounds(bounds, { padding: [50, 50] });
  }, [locations, map]);

  return null;
}

export function VisitorMap({ siteId, locations, newVisitorIps, onLocationClick }: VisitorMapProps) {
  const [mapLocations, setMapLocations] = useState<MapLocation[]>(locations);

  useEffect(() => {
    setMapLocations(locations);
  }, [locations]);

  // Default center (world view)
  const defaultCenter: LatLngExpression = [20, 0];
  const defaultZoom = 2;

  const formatLastSeen = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (mapLocations.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-slate-500 text-sm">No visitor locations to display yet</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[500px] rounded-lg overflow-hidden border border-slate-200">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds locations={mapLocations} />

        {mapLocations.map((location, idx) => {
          const isNew = newVisitorIps?.has(location.ip) || false;
          return (
            <Marker
              key={`${location.ip}-${idx}`}
              position={[location.lat, location.lon]}
              icon={createCustomIcon(location, isNew)}
              eventHandlers={{
                click: () => onLocationClick?.(location),
              }}
            >
            <Popup>
              <div className="p-2 min-w-[250px]">
                {/* Company Info */}
                {location.company_name ? (
                  <div className="mb-2 pb-2 border-b border-slate-200">
                    <div className="font-semibold text-slate-900 text-base">
                      {location.company_name}
                    </div>
                    {location.company_domain && (
                      <div className="text-xs text-blue-600 hover:underline">
                        <a href={`https://${location.company_domain}`} target="_blank" rel="noopener noreferrer">
                          {location.company_domain}
                        </a>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Location Info */}
                <div className="space-y-1 text-xs">
                  <div className="font-medium text-slate-700">
                    📍 {location.city}, {location.country}
                    {location.country_code && <span className="ml-1 text-slate-500">({location.country_code})</span>}
                  </div>

                  <div className="text-slate-600">
                    <span className="font-medium">Coordinates:</span> {location.lat.toFixed(4)}, {location.lon.toFixed(4)}
                  </div>

                  {location.geo_timezone && (
                    <div className="text-slate-600">
                      <span className="font-medium">Timezone:</span> {location.geo_timezone}
                    </div>
                  )}

                  {location.geo_currency && (
                    <div className="text-slate-600">
                      <span className="font-medium">Currency:</span> {location.geo_currency}
                    </div>
                  )}

                  {location.geo_isp && (
                    <div className="text-slate-600">
                      <span className="font-medium">ISP:</span> {location.geo_isp}
                    </div>
                  )}

                  <div className="text-slate-600">
                    <span className="font-medium">IP:</span> <code className="text-[10px]">{location.ip}</code>
                  </div>

                  {/* Fraud Flags */}
                  {(location.geo_is_mobile === 1 || location.geo_is_vpn === 1 || location.geo_is_datacenter === 1) && (
                    <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                      {location.geo_is_mobile === 1 && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-700">
                          📱 Mobile
                        </span>
                      )}
                      {location.geo_is_vpn === 1 && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          🔒 VPN
                        </span>
                      )}
                      {location.geo_is_datacenter === 1 && (
                        <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                          🤖 Bot
                        </span>
                      )}
                    </div>
                  )}

                  {/* Visit Stats */}
                  <div className="mt-2 pt-2 border-t border-slate-100 text-slate-500">
                    <div><span className="font-medium">Visits:</span> {location.visit_count}</div>
                    <div><span className="font-medium">Last seen:</span> {formatLastSeen(location.last_seen)}</div>
                    {location.last_url && (
                      <div className="mt-1">
                        <span className="font-medium">Latest page:</span>{" "}
                        <span className="text-blue-600 font-mono text-[11px]">
                          {(() => {
                            try {
                              return new URL(location.last_url).pathname || "/";
                            } catch {
                              return "/";
                            }
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 text-xs z-[1000]">
        <div className="font-semibold text-slate-700 mb-2">Legend</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span>Company</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span>Visitor</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-purple-500"></div>
            <span>Mobile</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-500"></div>
            <span>VPN</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span>Bot/Datacenter</span>
          </div>
        </div>
      </div>
    </div>
  );
}
